import { fetchCounts } from '@/atoms/stats'
import { Skeleton } from '@/components/ui/skeleton'

import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import MainNoteCard from './MainNoteCard'
import ReactionNoteCard from './ReactionNoteCard'
import RepostNoteCard from './RepostNoteCard'

export default function NoteCard({
  event,
  className,
  filterMutedNotes = true,
  pinned = false,
  reposters,
  repostEvent,
  groupedNotesTotalCount,
  onAllNotesRead,
  areAllNotesRead,
  displayScore,
  clamp = false
}: {
  event: Event
  className?: string
  filterMutedNotes?: boolean
  pinned?: boolean
  reposters?: string[]
  repostEvent?: Event
  groupedNotesTotalCount?: number
  onAllNotesRead?: () => void
  areAllNotesRead?: boolean
  displayScore?: number
  clamp?: boolean
}) {
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const shouldHide = useMemo(() => {
    if (filterMutedNotes && mutePubkeySet.has(event.pubkey)) {
      return true
    }
    return false
  }, [event, filterMutedNotes, mutePubkeySet])
  if (shouldHide) return null

  const containerRef = useRef<HTMLDivElement>(null)
  const hasFetched = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFetched.current) {
          timerRef.current = setTimeout(() => {
            hasFetched.current = true
            fetchCounts(event)
            observer.unobserve(el)
          }, 2000)
        } else if (!entry.isIntersecting && timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = undefined
        }
      },
      { threshold: 0 }
    )

    observer.observe(el)
    return () => {
      observer.disconnect()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [event.id])

  let card: ReactNode = null
  if (event.kind === kinds.Reaction) {
    card = (
      <ReactionNoteCard
        event={event}
        className={className}
        filterMutedNotes={filterMutedNotes}
        pinned={pinned}
        groupedNotesTotalCount={groupedNotesTotalCount}
        onAllNotesRead={onAllNotesRead}
        areAllNotesRead={areAllNotesRead}
        displayScore={displayScore}
        clamp={clamp}
      />
    )
  } else if (event.kind === kinds.Repost) {
    card = (
      <RepostNoteCard
        event={event}
        className={className}
        filterMutedNotes={filterMutedNotes}
        pinned={pinned}
        groupedNotesTotalCount={groupedNotesTotalCount}
        onAllNotesRead={onAllNotesRead}
        areAllNotesRead={areAllNotesRead}
        displayScore={displayScore}
        clamp={clamp}
      />
    )
  } else {
    card = (
      <MainNoteCard
        event={event}
        className={className}
        pinned={pinned}
        reposters={reposters}
        repostEvent={repostEvent}
        groupedNotesTotalCount={groupedNotesTotalCount}
        onAllNotesRead={onAllNotesRead}
        areAllNotesRead={areAllNotesRead}
        displayScore={displayScore}
        clamp={clamp}
      />
    )
  }
  return <div ref={containerRef}>{card}</div>
}

export function NoteCardLoadingSkeleton() {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center space-x-2">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className={`flex-1 w-0`}>
          <div className="py-1">
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="py-0.5">
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
      </div>
      <div className="pt-2">
        <div className="my-1">
          <Skeleton className="w-full h-4 my-1 mt-2" />
        </div>
        <div className="my-1">
          <Skeleton className="w-2/3 h-4 my-1" />
        </div>
      </div>
    </div>
  )
}
